// File database operations for IndexedDB
let fileDB: IDBDatabase | null = null;

// Initialize IndexedDB
export const initializeFileDB = (): Promise<IDBDatabase> => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    console.log('Initializing IndexedDB for files...');
    const request = indexedDB.open('PowerAIFiles', 3); // Increment version to force upgrade
    
    request.onerror = (event) => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };
    
    request.onupgradeneeded = (event) => {
      console.log('Upgrading IndexedDB schema...');
      const db = (event.target as IDBOpenDBRequest).result;
      
      // If the object store exists, delete it first
      if (db.objectStoreNames.contains('files')) {
        db.deleteObjectStore('files');
      }
      
      // Create a new object store
      const store = db.createObjectStore('files', { keyPath: 'filename' });
      console.log('Created files object store');
    };
    
    request.onsuccess = () => {
      console.log('IndexedDB initialized successfully');
      fileDB = request.result;
      resolve(request.result);
    };
  });
};

// Store file helper function
export const storeFile = async (filename: string, file: File): Promise<void> => {
  if (!fileDB) {
    console.log('Initializing IndexedDB for files...');
    fileDB = await initializeFileDB();
  }
  console.log('Storing file:', filename, {
    type: file.type,
    size: file.size
  });
  
  return new Promise<void>((resolve, reject) => {
    const transaction = fileDB!.transaction(['files'], 'readwrite');
    const store = transaction.objectStore('files');
    
    // Check if the file already exists 
    const getRequest = store.get(filename);
    
    getRequest.onsuccess = () => {
      if (getRequest.result) {
        console.log('File already exists in IndexedDB, updating:', filename);
      } else {
        console.log('Adding new file to IndexedDB:', filename);
      }
      
      const putRequest = store.put({ filename, file });
      
      putRequest.onsuccess = () => {
        console.log('File stored successfully:', filename);
        resolve();
      };
      
      putRequest.onerror = () => {
        console.error('Error storing file:', putRequest.error);
        reject(putRequest.error);
      };
    };
    
    getRequest.onerror = () => {
      console.error('Error checking file existence:', getRequest.error);
      reject(getRequest.error);
    };
  });
};

// Retrieve every stored file. Used by the data export, which must not miss
// attachments that are no longer referenced by any message.
export const retrieveAllFiles = async (): Promise<Array<{ filename: string; file: File }>> => {
  if (!fileDB) {
    fileDB = await initializeFileDB();
  }

  return new Promise<Array<{ filename: string; file: File }>>((resolve, reject) => {
    const transaction = fileDB!.transaction(['files'], 'readonly');
    const store = transaction.objectStore('files');
    const request = store.getAll();

    request.onsuccess = () => {
      const rows = (request.result || []) as Array<{ filename: string; file: File }>;
      resolve(rows.filter((row) => row && row.file));
    };

    request.onerror = () => {
      console.error('Error retrieving all files:', request.error);
      reject(request.error);
    };
  });
};

// Retrieve file helper function
export const retrieveFile = async (filename: string): Promise<File | undefined> => {
  if (!fileDB) {
    console.log('Initializing IndexedDB for files...');
    fileDB = await initializeFileDB();
  }
  
  return new Promise<File | undefined>((resolve, reject) => {
    const transaction = fileDB!.transaction(['files'], 'readonly');
    const store = transaction.objectStore('files');
    
    console.log('Retrieving file from IndexedDB:', filename);
    const request = store.get(filename);
    
    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        console.log('File retrieved successfully:', filename, {
          type: result.file.type,
          size: result.file.size
        });
        resolve(result.file);
      } else {
        console.log('File not found in IndexedDB:', filename);
        resolve(undefined);
      }
    };
    
    request.onerror = () => {
      console.error('Error retrieving file:', request.error);
      reject(request.error);
    };
  });
};